import React, { useState } from "react";
import { AtSign, Copy, Check, Wand2, Send, KeyRound, LogOut, Loader2, X, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { formatGBP } from "@/lib/marketplace";

/**
 * KidActionsToolbar — grouped management controls for a single kid, used on
 * the Child Management (KidProfile) page and anywhere else a parent needs
 * quick access. Pattern-matches the ParentDashboard kid-card affordances so
 * behaviour stays consistent.
 *
 * Actions:
 *   • Copy Handle (writes @handle to clipboard)
 *   • Regenerate Handle (confirmed)
 *   • Top Up Allowance (custom amount modal → hands off via `onTopUp`)
 *   • Reset Kid Zone PIN (opens `onResetPin`)
 *   • Sign kid out on all devices (confirmed)
 *
 * The parent page owns wallet + PIN flows via callbacks; ambiguous actions
 * (regenerate, revoke) resolve locally with their own confirm modals.
 */
export default function KidActionsToolbar({
  kid,                    // {id, name, handle}
  walletAvailablePence,   // household wallet cap
  onTopUp,                // (kidLike, pennies) => void — opens PIN modal
  onResetPin,             // () => void — opens the shared PIN reset flow
  onHandleUpdated,        // (newHandle) => void — bubble the new handle up
  onFlash,                // (msg) => void — inline flash on the parent page
}) {
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(""); // "" | "regen" | "revoke"
  const [err, setErr] = useState("");
  const [confirmRegen, setConfirmRegen] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [topUpOpen, setTopUpOpen] = useState(false);
  const [amount, setAmount] = useState("1.00");
  const [topUpErr, setTopUpErr] = useState("");

  if (!kid?.id) return null;

  const copyHandle = async () => {
    if (!kid.handle) return;
    try {
      await navigator.clipboard.writeText(`@${kid.handle}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_e) { /* clipboard blocked */ }
  };

  const regenHandle = async () => {
    setBusy("regen"); setErr("");
    try {
      const { data } = await api.post(`/kids/${kid.id}/ensure-handle`, { regenerate: true });
      const newHandle = data?.handle || data?.kid?.handle;
      if (newHandle) {
        onHandleUpdated?.(newHandle);
        onFlash?.(`New handle: @${newHandle}`);
      }
      setConfirmRegen(false);
    } catch (e) {
      setErr(formatApiError(e) || "Couldn't regenerate handle. Try again.");
    } finally { setBusy(""); }
  };

  const revokeSessions = async () => {
    setBusy("revoke"); setErr("");
    try {
      await api.post(`/kids/${kid.id}/revoke-sessions`);
      onFlash?.(`Signed ${kid.name} out on every device.`);
      setConfirmRevoke(false);
    } catch (e) {
      setErr(formatApiError(e) || "Couldn't sign kid out on all devices.");
    } finally { setBusy(""); }
  };

  const submitTopUp = (e) => {
    e.preventDefault();
    setTopUpErr("");
    const parsed = parseFloat(amount);
    if (!(parsed > 0)) { setTopUpErr("Enter an amount greater than £0."); return; }
    if (parsed > 250) { setTopUpErr("Single top-ups are capped at £250."); return; }
    const pennies = Math.round(parsed * 100);
    if (pennies > walletAvailablePence) {
      setTopUpErr(`Household wallet only has ${formatGBP(walletAvailablePence / 100)}. Top up first.`);
      return;
    }
    setTopUpOpen(false);
    onTopUp?.(kid, pennies);
  };

  return (
    <div className="card-base p-5 md:p-6" data-testid="kid-actions-toolbar">
      <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-3">Manage</div>

      {kid.handle && (
        <div className="mb-4 rounded-2xl bg-gradient-to-br from-brandTeal/10 via-white to-transparent border border-brandTeal/25 px-4 py-3">
          <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-brandTeal">Kid Zone handle</div>
          <div
            className="font-mono font-bold text-brandTealDark text-lg md:text-xl truncate leading-tight mt-0.5"
            data-testid="kid-actions-handle-value"
            title={`@${kid.handle}`}
          >
            @{kid.handle}
          </div>
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={copyHandle}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-brandTeal hover:bg-brandTealDark text-white text-xs font-bold shadow-sm transition"
              data-testid="kid-actions-copy-handle"
            >
              {copied ? (<><Check className="w-3.5 h-3.5" /> Copied</>) : (<><Copy className="w-3.5 h-3.5" /> Copy Handle</>)}
            </button>
            <button
              type="button"
              onClick={() => setConfirmRegen(true)}
              disabled={busy === "regen"}
              className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-white border border-brandTeal/30 hover:border-brandTeal text-brandTeal transition disabled:opacity-40 flex-shrink-0"
              data-testid="kid-actions-regen-handle"
              title="Regenerate handle"
              aria-label="Regenerate handle"
            >
              {busy === "regen" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
            </button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <button
          type="button"
          onClick={() => { setAmount("1.00"); setTopUpErr(""); setTopUpOpen(true); }}
          className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-brandPink hover:bg-brandPinkDark text-white font-bold text-sm shadow-sm transition"
          data-testid="kid-actions-topup"
        >
          <Send className="w-4 h-4" /> Top Up Allowance
        </button>
        <button
          type="button"
          onClick={() => onResetPin?.()}
          className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-white border border-slate-200 hover:border-brandTeal hover:text-brandTeal font-bold text-sm text-slate-700 transition"
          data-testid="kid-actions-reset-pin"
        >
          <KeyRound className="w-4 h-4" /> Reset Kid Zone PIN
        </button>
        <button
          type="button"
          onClick={() => setConfirmRevoke(true)}
          className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-white border border-slate-200 hover:border-rose-300 hover:text-rose-700 font-bold text-sm text-slate-700 transition"
          data-testid="kid-actions-revoke-sessions"
        >
          <LogOut className="w-4 h-4" /> Sign kid out on all devices
        </button>
      </div>

      {err && (
        <div className="mt-3 rounded-xl bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-800" data-testid="kid-actions-error">{err}</div>
      )}

      {/* Regenerate confirm */}
      <Dialog open={confirmRegen} onOpenChange={(o) => { if (!o && busy !== "regen") setConfirmRegen(false); }}>
        <DialogContent className="sm:max-w-md rounded-3xl border-0 p-0 overflow-hidden" data-testid="kid-actions-regen-modal">
          <div className="px-6 pt-6 pb-4 bg-gradient-to-br from-amber-50 to-white border-b border-slate-100">
            <div className="pill bg-amber-100 text-amber-800"><AlertTriangle className="w-3 h-3 mr-1" /> Regenerate handle</div>
            <DialogHeader className="mt-2 text-left">
              <DialogTitle className="font-display text-2xl font-semibold text-brandSlate">Regenerate @{kid.handle}?</DialogTitle>
              <DialogDescription className="text-sm text-slate-600 leading-relaxed">
                {kid.name} will need the new handle to sign back into Kid Zone.
              </DialogDescription>
            </DialogHeader>
          </div>
          <div className="px-6 pt-5 pb-6 flex gap-2">
            <button type="button" onClick={() => setConfirmRegen(false)} className="flex-1 btn-secondary" data-testid="kid-actions-regen-cancel">Cancel</button>
            <button type="button" onClick={regenHandle} disabled={busy === "regen"} className="flex-1 btn-primary" data-testid="kid-actions-regen-confirm">
              {busy === "regen" ? (<><Loader2 className="w-4 h-4 animate-spin" /> Working&hellip;</>) : (<><Wand2 className="w-4 h-4" /> Regenerate</>)}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Revoke sessions confirm */}
      <Dialog open={confirmRevoke} onOpenChange={(o) => { if (!o && busy !== "revoke") setConfirmRevoke(false); }}>
        <DialogContent className="sm:max-w-md rounded-3xl border-0 p-0 overflow-hidden" data-testid="kid-actions-revoke-modal">
          <div className="px-6 pt-6 pb-4 bg-gradient-to-br from-rose-50 to-white border-b border-slate-100">
            <div className="pill bg-rose-100 text-rose-800"><AlertTriangle className="w-3 h-3 mr-1" /> Sign out everywhere</div>
            <DialogHeader className="mt-2 text-left">
              <DialogTitle className="font-display text-2xl font-semibold text-brandSlate">Sign {kid.name} out everywhere?</DialogTitle>
              <DialogDescription className="text-sm text-slate-600 leading-relaxed">
                This ends every active Kid Zone session. Their PIN stays the same &mdash; they just need to sign back in next time.
              </DialogDescription>
            </DialogHeader>
          </div>
          <div className="px-6 pt-5 pb-6 flex gap-2">
            <button type="button" onClick={() => setConfirmRevoke(false)} className="flex-1 btn-secondary" data-testid="kid-actions-revoke-cancel">Cancel</button>
            <button type="button" onClick={revokeSessions} disabled={busy === "revoke"} className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-bold text-sm disabled:opacity-60 transition" data-testid="kid-actions-revoke-confirm">
              {busy === "revoke" ? (<><Loader2 className="w-4 h-4 animate-spin" /> Signing out&hellip;</>) : (<><LogOut className="w-4 h-4" /> Sign out</>)}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Custom Top-Up modal */}
      <Dialog open={topUpOpen} onOpenChange={setTopUpOpen}>
        <DialogContent className="sm:max-w-md rounded-3xl border-0 p-0 overflow-hidden" data-testid="kid-actions-topup-modal">
          <div className="px-6 pt-6 pb-4 bg-gradient-to-br from-brandTeal/10 via-white to-brandPink/10 border-b border-slate-100">
            <div className="pill bg-brandTeal/15 text-brandTeal"><Send className="w-3 h-3 mr-1" /> Top Up Allowance</div>
            <DialogHeader className="mt-2 text-left">
              <DialogTitle className="font-display text-2xl font-semibold text-brandSlate">Send pocket money to {kid.name}</DialogTitle>
              <DialogDescription className="text-sm text-slate-600 leading-relaxed">
                Confirm with your PIN and it lands in their Piggy Bank instantly.
              </DialogDescription>
            </DialogHeader>
          </div>
          <form onSubmit={submitTopUp} noValidate className="px-6 pt-5 pb-6 space-y-4">
            <div>
              <label className="block text-sm font-bold text-brandSlate mb-1">Amount</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-bold">£</span>
                <input
                  type="number" min="0.5" step="0.5" inputMode="decimal"
                  value={amount} onChange={(e) => setAmount(e.target.value)}
                  className="input-base pl-7" autoFocus
                  data-testid="kid-actions-topup-amount"
                />
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {["1", "2", "5", "10"].map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setAmount(`${v}.00`)}
                    className="px-3 py-1 rounded-full bg-slate-100 hover:bg-brandTeal/10 hover:text-brandTeal text-xs font-bold text-slate-700 transition"
                    data-testid={`kid-actions-topup-quick-${v}`}
                  >£{v}</button>
                ))}
              </div>
              <p className="text-[11px] text-slate-500 mt-2">
                Household wallet: <span className="font-bold text-slate-700">{formatGBP(walletAvailablePence / 100)}</span>
              </p>
            </div>
            {topUpErr && (
              <div className="rounded-xl bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-800" data-testid="kid-actions-topup-error">{topUpErr}</div>
            )}
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={() => setTopUpOpen(false)} className="flex-1 btn-secondary" data-testid="kid-actions-topup-cancel">
                <X className="w-4 h-4" /> Cancel
              </button>
              <button type="submit" className="flex-1 btn-primary" data-testid="kid-actions-topup-submit">
                <Check className="w-4 h-4" /> Send with PIN
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Small named export used by KidProfile to render the @handle badge under
 *  the child's name in the header column. Keeps the JSX tidy. */
export function KidHandleBadge({ handle, size = "sm" }) {
  if (!handle) return null;
  const px = size === "lg" ? "px-3.5 py-2 text-base" : "px-3 py-1.5 text-sm";
  return (
    <div className={`inline-flex items-center gap-1.5 rounded-2xl bg-gradient-to-br from-brandTeal to-brandTealDark text-white font-mono font-bold shadow-sm ${px}`} data-testid="kid-profile-handle-badge">
      <AtSign className="w-4 h-4 opacity-80" />
      <span data-testid="kid-profile-handle-value">{handle}</span>
    </div>
  );
}
